import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";
import E8SquareGridAreaDiagram from "../../../../components/diagrams/E8SquareGridAreaDiagram";
import { scoreStaged } from "../../../openTaskScoring";

function pick(values) {
  return values[Math.floor(Math.random() * values.length)];
}

function buildData({ side, horizontalParts, verticalParts, fFromD, eFromB }) {
  const horizontalUnit = side / horizontalParts;
  const verticalUnit = side / verticalParts;
  const df = fFromD * horizontalUnit;
  const be = eFromB * verticalUnit;
  const squareArea = side * side;
  const triangleADF = (df * side) / 2;
  const triangleABE = (be * side) / 2;
  const area = squareArea - triangleADF - triangleABE;

  return {
    side,
    horizontalParts,
    verticalParts,
    fFromD,
    eFromB,
    horizontalUnit,
    verticalUnit,
    df,
    be,
    squareArea,
    triangleADF,
    triangleABE,
    area,
    maxPoints: 3,
  };
}

function createOriginalTask() {
  return {
    type: "E8SquareGridQuadrilateralAreaOpenTask",
    data: buildData({
      side: 15,
      horizontalParts: 3,
      verticalParts: 5,
      fFromD: 2,
      eFromB: 2,
    }),
    source: {
      variant: "original",
      exam: "E8",
      session: "2025_MAJ",
      taskNumber: 20,
    },
    tags: ["e8", "planimetria", "czworokaty", "kwadrat", "pole", "zadanie-otwarte"],
  };
}

const RANDOM_CONFIGS = [
  { side: 12, horizontalParts: 3, verticalParts: 4 },
  { side: 15, horizontalParts: 3, verticalParts: 5 },
  { side: 18, horizontalParts: 3, verticalParts: 6 },
  { side: 20, horizontalParts: 4, verticalParts: 5 },
  { side: 24, horizontalParts: 4, verticalParts: 6 },
  { side: 30, horizontalParts: 5, verticalParts: 6 },
];

function createRandomData() {
  const candidates = [];

  RANDOM_CONFIGS.forEach(({ side, horizontalParts, verticalParts }) => {
    for (let fFromD = 1; fFromD < horizontalParts; fFromD += 1) {
      for (let eFromB = 1; eFromB < verticalParts; eFromB += 1) {
        const data = buildData({ side, horizontalParts, verticalParts, fFromD, eFromB });
        const allIntegers = [
          data.horizontalUnit,
          data.verticalUnit,
          data.df,
          data.be,
          data.triangleADF,
          data.triangleABE,
          data.area,
        ].every(Number.isInteger);
        if (
          allIntegers &&
          data.df !== data.be &&
          data.area > 0 &&
          data.area < data.squareArea
        ) {
          candidates.push(data);
        }
      }
    }
  });

  return pick(candidates);
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  return {
    type: "E8SquareGridQuadrilateralAreaOpenTask",
    data: createRandomData(),
    source: { variant: "random", basedOn: "E8_2025_MAJ_20" },
    tags: ["e8", "planimetria", "czworokaty", "kwadrat", "pole", "zadanie-otwarte"],
  };
}

function getRaw(value) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value.raw === "string") return value.raw;
  if (typeof value.value === "string") return value.value;
  if (typeof value.parsed === "number") return String(value.parsed);
  return "";
}

function parseNum(value) {
  const raw = getRaw(value).trim().replace(",", ".");
  if (!raw) return null;
  const numeric = Number(raw);
  return Number.isFinite(numeric) ? numeric : null;
}

function isNumCorrect(value, expected) {
  const parsed = parseNum(value);
  return parsed != null && Math.abs(parsed - expected) < 0.001;
}

function AreaInputRow({ label, value, onChange, disabled }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ opacity: 0.9 }}>{label}</span>
      <FractionInput
        value={value}
        onChange={onChange}
        disabled={disabled}
        label=""
        suffixLatex={"\\text{ cm}^2"}
        placeholder="np. 105"
      />
    </div>
  );
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.65 }}>
      <p>
        Dany jest kwadrat <InlineMath math="ABCD" /> o boku długości{" "}
        <InlineMath math={`${data.side}\\text{ cm}`} />. Każdy z boków{" "}
        <InlineMath math="AB" /> i <InlineMath math="CD" /> podzielono na{" "}
        <InlineMath math={`${data.horizontalParts}`} /> równe części, a każdy z boków{" "}
        <InlineMath math="AD" /> i <InlineMath math="BC" /> podzielono na{" "}
        <InlineMath math={`${data.verticalParts}`} /> równych części.
      </p>
      <p>
        Na boku <InlineMath math="BC" /> zaznaczono punkt <InlineMath math="E" />, na boku{" "}
        <InlineMath math="CD" /> zaznaczono punkt <InlineMath math="F" />, a ponadto
        poprowadzono odcinki <InlineMath math="AE" /> i <InlineMath math="AF" />.
      </p>
      <E8SquareGridAreaDiagram data={data} />
      <p>
        <strong>
          Oblicz pole czworokąta <InlineMath math="AECF" />. Zapisz obliczenia.
        </strong>
      </p>
    </div>
  );
}

export function renderInput(task, userAnswer = {}, onChange, disabled) {
  const setField = (field) => (value) => onChange({ ...(userAnswer || {}), [field]: value });
  return (
    <div style={{ display: "grid", gap: 14, textAlign: "left", marginTop: 10 }}>
      <div
        style={{
          fontSize: 12.5,
          opacity: 0.78,
          lineHeight: 1.45,
          borderLeft: "3px solid var(--color-accent)",
          paddingLeft: 8,
        }}
      >
        <strong>{"Wyniki po\u015brednie s\u0105 opcjonalne."}</strong>{" "}
        {
          "Wpisz je, je\u015bli chcesz pokaza\u0107 tok rozwi\u0105zania i dosta\u0107 punkty cz\u0105stkowe, gdyby wynik ko\u0144cowy by\u0142 niepoprawny. Poprawne pole czworok\u0105ta z jednostk\u0105 daje pe\u0142ne 3 punkty."
        }
      </div>
      <AreaInputRow
        label="Pole kwadratu ABCD:"
        value={userAnswer.squareArea}
        onChange={setField("squareArea")}
        disabled={disabled}
      />
      <AreaInputRow
        label="Pole trójkąta ABE:"
        value={userAnswer.triangleABE}
        onChange={setField("triangleABE")}
        disabled={disabled}
      />
      <AreaInputRow
        label="Pole trójkąta ADF:"
        value={userAnswer.triangleADF}
        onChange={setField("triangleADF")}
        disabled={disabled}
      />
      <AreaInputRow
        label="Pole czworokąta AECF:"
        value={userAnswer.area}
        onChange={setField("area")}
        disabled={disabled}
      />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer = {}) {
  const parts = {
    "pole kwadratu ABCD": isNumCorrect(userAnswer.squareArea, data.squareArea),
    "pole trójkąta ABE": isNumCorrect(userAnswer.triangleABE, data.triangleABE),
    "pole trójkąta ADF": isNumCorrect(userAnswer.triangleADF, data.triangleADF),
    "pole czworokąta AECF": isNumCorrect(userAnswer.area, data.area),
  };
  return scoreStaged({
    resultOk: parts["pole czworokąta AECF"],
    milestones: [
      parts["pole kwadratu ABCD"],
      parts["pole trójkąta ABE"] && parts["pole trójkąta ADF"],
    ],
    maxScore: data.maxPoints,
    parts,
  });
}

export function renderAnswer({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>{"Przyk\u0142adowa poprawna odpowied\u017a:"}</div>
      <BlockMath math={`P_{ABCD}=${data.side}^2=${data.squareArea}\\text{ cm}^2`} />
      <BlockMath math={`P_{ABE}=\\frac{${data.be}\\cdot ${data.side}}{2}=${data.triangleABE}\\text{ cm}^2`} />
      <BlockMath math={`P_{ADF}=\\frac{${data.df}\\cdot ${data.side}}{2}=${data.triangleADF}\\text{ cm}^2`} />
      <BlockMath math={`P_{AECF}=${data.squareArea}-${data.triangleABE}-${data.triangleADF}=${data.area}\\text{ cm}^2`} />
      <div>{"Odpowied\u017a: "}<InlineMath math={`P_{AECF}=${data.area}\\text{ cm}^2`} />{"."}</div>
    </div>
  );
}

function areaAnswerLatex(value) {
  const parsed = parseNum(value);
  return parsed == null ? "\\text{brak}" : `${parsed}\\text{ cm}^2`;
}

export function renderUserAnswer(userAnswer = {}) {
  return (
    <div style={{ textAlign: "left", display: "grid", gap: 4 }}>
      <div><InlineMath math={`P_{ABCD}=${areaAnswerLatex(userAnswer.squareArea)}`} /></div>
      <div><InlineMath math={`P_{ABE}=${areaAnswerLatex(userAnswer.triangleABE)}`} /></div>
      <div><InlineMath math={`P_{ADF}=${areaAnswerLatex(userAnswer.triangleADF)}`} /></div>
      <div><InlineMath math={`P_{AECF}=${areaAnswerLatex(userAnswer.area)}`} /></div>
    </div>
  );
}

export function renderExplanation({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> Wyznaczamy długości odcinków potrzebnych do
        obliczenia pól trójkątów prostokątnych.
      </p>
      <BlockMath
        math={`${data.side}:${data.horizontalParts}=${data.horizontalUnit},\\quad DF=${data.fFromD}\\cdot ${data.horizontalUnit}=${data.df}`}
      />
      <BlockMath
        math={`${data.side}:${data.verticalParts}=${data.verticalUnit},\\quad BE=${data.eFromB}\\cdot ${data.verticalUnit}=${data.be}`}
      />
      <p>
        <strong>Krok 2.</strong> Obliczamy pole całego kwadratu.
      </p>
      <BlockMath math={`P_{ABCD}=${data.side}^2=${data.squareArea}\\text{ cm}^2`} />
      <p>
        <strong>Krok 3.</strong> Od kwadratu odejmiemy pola dwóch trójkątów:
        dolnego <InlineMath math="ABE" /> i lewego <InlineMath math="ADF" />.
      </p>
      <BlockMath math={`P_{ABE}=\\frac{${data.be}\\cdot ${data.side}}{2}=${data.triangleABE}\\text{ cm}^2`} />
      <BlockMath math={`P_{ADF}=\\frac{${data.df}\\cdot ${data.side}}{2}=${data.triangleADF}\\text{ cm}^2`} />
      <p>
        <strong>Krok 4.</strong> Pole szukanego czworokąta to pole kwadratu
        pomniejszone o pola tych dwóch trójkątów.
      </p>
      <BlockMath
        math={`P_{AECF}=${data.squareArea}-${data.triangleABE}-${data.triangleADF}=${data.area}\\text{ cm}^2`}
      />
    </div>
  );
}
